import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActionIcon, Badge, Box, Button, Drawer, Group, Loader, Paper, Select, Stack,
  Text, Textarea, Tooltip, UnstyledButton,
} from '@mantine/core';
import { TimeInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import {
  IconChevronLeft, IconChevronRight, IconPlus, IconSend, IconTrash,
} from '@tabler/icons-react';
import { isAxiosError } from 'axios';
import { PageHeader } from '../../components/PageHeader';
import { useBranches } from '../branches/branchesApi';
import {
  useRosterWeek, useCreateAssignment, useUpdateAssignment, useDeleteAssignment, usePublishRoster,
  type ShiftAssignmentDto, type ShiftTemplateDto,
} from './schedulingApi';

const DAY_MS = 86_400_000;
const hhmm = (t: string) => t.slice(0, 5);
const toTimeOnly = (t: string) => (t.length === 5 ? `${t}:00` : t);
const toIso = (d: Date) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};
// Snap to the Saturday that opens this date's week (regional week start).
const startOfWeek = (d: Date) => {
  const copy = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const offset = (copy.getDay() - 6 + 7) % 7;
  return new Date(copy.getTime() - offset * DAY_MS);
};
const fmtMoney = (n: number) =>
  new Intl.NumberFormat('ar-PS', { minimumFractionDigits: 2, numberingSystem: 'latn' }).format(n);

interface DrawerState {
  mode: 'create' | 'edit';
  employeeId: string;
  workDate: string;
  assignment?: ShiftAssignmentDto;
}

export function RosterPage() {
  const { t, i18n } = useTranslation();
  const ar = i18n.language === 'ar';

  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [branchId, setBranchId] = useState<string | null>(null);

  const days = useMemo(
    () => Array.from({ length: 7 }, (_, i) => new Date(weekStart.getTime() + i * DAY_MS)),
    [weekStart],
  );
  const fromIso = toIso(days[0]!);
  const toIsoStr = toIso(days[6]!);

  const { data, isLoading } = useRosterWeek(fromIso, toIsoStr, branchId);
  const { data: branches } = useBranches();
  const create = useCreateAssignment();
  const update = useUpdateAssignment();
  const del = useDeleteAssignment();
  const publish = usePublishRoster();

  const [drawer, setDrawer] = useState<DrawerState | null>(null);

  const templates = data?.templates ?? [];
  const employees = data?.employees ?? [];
  const branchOptions = (branches ?? []).map((b) => ({ value: b.branchId, label: ar ? b.nameAr : b.nameEn ?? b.nameAr }));
  const empName = (e: { nameAr: string; nameEn: string | null }) => (ar ? e.nameAr : e.nameEn ?? e.nameAr);
  const tmplName = (a: ShiftAssignmentDto) => (ar ? a.templateNameAr : a.templateNameEn ?? a.templateNameAr);

  // employeeId → workDate → assignments
  const grid = useMemo(() => {
    const map = new Map<string, Map<string, ShiftAssignmentDto[]>>();
    for (const a of data?.assignments ?? []) {
      if (!map.has(a.employeeId)) map.set(a.employeeId, new Map());
      const byDay = map.get(a.employeeId)!;
      if (!byDay.has(a.workDate)) byDay.set(a.workDate, []);
      byDay.get(a.workDate)!.push(a);
    }
    return map;
  }, [data]);

  const weekdayFmt = new Intl.DateTimeFormat(ar ? 'ar-PS' : 'en', { weekday: 'short' });
  const dayNumFmt = new Intl.DateTimeFormat(ar ? 'ar-PS' : 'en', { day: 'numeric', month: 'short', numberingSystem: 'latn' });
  const todayIso = toIso(new Date());

  const form = useForm({
    initialValues: { shiftTemplateId: null as string | null, startTime: '08:00', endTime: '16:00', notes: '' },
    validate: {
      startTime: (v) => (v ? null : t('common.errors.required')),
      endTime: (v) => (v ? null : t('common.errors.required')),
    },
  });

  const openCreate = (employeeId: string, workDate: string) => {
    form.setValues({ shiftTemplateId: null, startTime: '08:00', endTime: '16:00', notes: '' });
    form.resetDirty();
    setDrawer({ mode: 'create', employeeId, workDate });
  };
  const openEdit = (a: ShiftAssignmentDto) => {
    form.setValues({ shiftTemplateId: a.shiftTemplateId, startTime: hhmm(a.startTime), endTime: hhmm(a.endTime), notes: a.notes ?? '' });
    form.resetDirty();
    setDrawer({ mode: 'edit', employeeId: a.employeeId, workDate: a.workDate, assignment: a });
  };

  const onTemplateChange = (id: string | null) => {
    form.setFieldValue('shiftTemplateId', id);
    const tpl = templates.find((x) => x.shiftTemplateId === id);
    if (tpl) form.setValues({ shiftTemplateId: id, startTime: hhmm(tpl.startTime), endTime: hhmm(tpl.endTime), notes: form.values.notes });
  };

  const submit = form.onSubmit(async (v) => {
    if (!drawer) return;
    const base = {
      shiftTemplateId: v.shiftTemplateId, branchId, workDate: drawer.workDate,
      startTime: toTimeOnly(v.startTime), endTime: toTimeOnly(v.endTime), notes: v.notes.trim() || null,
    };
    try {
      if (drawer.mode === 'edit' && drawer.assignment) {
        await update.mutateAsync({ id: drawer.assignment.shiftAssignmentId, req: base });
      } else {
        await create.mutateAsync({ ...base, employeeId: drawer.employeeId });
      }
      setDrawer(null);
    } catch (err) {
      notifications.show({
        color: 'red', title: t('common.toasts.errorTitle'),
        message: (isAxiosError(err) && (err.response?.data?.detail as string)) || t('common.toasts.errorBody'),
      });
    }
  });

  const doPublish = async () => {
    try {
      const res = await publish.mutateAsync({ from: fromIso, to: toIsoStr });
      notifications.show({ color: 'green', title: t('hr.scheduling.roster.publishedTitle'), message: t('hr.scheduling.roster.publishedBody', { count: res.published }) });
    } catch {
      notifications.show({ color: 'red', title: t('common.toasts.errorTitle'), message: t('common.toasts.errorBody') });
    }
  };

  const chipColor = (a: ShiftAssignmentDto): string | undefined =>
    templates.find((x) => x.shiftTemplateId === a.shiftTemplateId)?.color ?? undefined;

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow={t('nav.sections.hr')}
        title={t('hr.scheduling.roster.title')}
        subtitle={t('hr.scheduling.roster.subtitle')}
        actions={
          <Button leftSection={<IconSend size={16} stroke={1.8} />} onClick={doPublish} loading={publish.isPending}>
            {t('hr.scheduling.roster.publish')}
          </Button>
        }
      />

      <Paper p="md">
        <Group justify="space-between" wrap="wrap" gap="md">
          <Group gap="xs">
            <ActionIcon variant="default" size="lg" aria-label={t('hr.scheduling.roster.prevWeek')}
              onClick={() => setWeekStart(new Date(weekStart.getTime() - 7 * DAY_MS))}>
              {ar ? <IconChevronRight size={18} /> : <IconChevronLeft size={18} />}
            </ActionIcon>
            <Button variant="subtle" onClick={() => setWeekStart(startOfWeek(new Date()))}>{t('hr.scheduling.roster.thisWeek')}</Button>
            <ActionIcon variant="default" size="lg" aria-label={t('hr.scheduling.roster.nextWeek')}
              onClick={() => setWeekStart(new Date(weekStart.getTime() + 7 * DAY_MS))}>
              {ar ? <IconChevronLeft size={18} /> : <IconChevronRight size={18} />}
            </ActionIcon>
            <Text fw={600} c="var(--wheels-ink-strong)" className="font-numeric">
              {dayNumFmt.format(days[0]!)} – {dayNumFmt.format(days[6]!)}
            </Text>
          </Group>
          <Group gap="md">
            <Select data={branchOptions} value={branchId} onChange={setBranchId} clearable
              placeholder={t('hr.scheduling.roster.allBranches')} style={{ minWidth: 180 }} />
            <Group gap={6}>
              <Text size="sm" c="var(--wheels-ink-muted)">{t('hr.scheduling.roster.totalHours')}</Text>
              <Badge variant="light" color="gray" radius="sm" className="font-numeric">{data?.totalHours ?? 0}</Badge>
              <Text size="sm" c="var(--wheels-ink-muted)">{t('hr.scheduling.roster.estCost')}</Text>
              <Badge variant="light" color="red" radius="sm" className="font-numeric">₪ {fmtMoney(data?.totalEstimatedCost ?? 0)}</Badge>
            </Group>
          </Group>
        </Group>
      </Paper>

      {isLoading ? (
        <Stack align="center" py={48}><Loader size="sm" /></Stack>
      ) : employees.length === 0 ? (
        <Paper p="lg"><Text ta="center" c="var(--wheels-ink-muted)" py="lg">{t('hr.scheduling.roster.noEmployees')}</Text></Paper>
      ) : (
        <Paper p={0} style={{ overflowX: 'auto' }}>
          <Box component="table" style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
            <thead>
              <tr>
                <Box component="th" style={thStyle('start', 220)}>{t('hr.scheduling.roster.employee')}</Box>
                {days.map((d) => (
                  <Box component="th" key={toIso(d)} style={thStyle('center')}>
                    <Text size="sm" fw={600} c={toIso(d) === todayIso ? 'var(--wheels-red, #B5191D)' : 'var(--wheels-ink-strong)'}>{weekdayFmt.format(d)}</Text>
                    <Text size="xs" c="var(--wheels-ink-muted)" className="font-numeric">{dayNumFmt.format(d)}</Text>
                  </Box>
                ))}
                <Box component="th" style={thStyle('end', 120)}>{t('hr.scheduling.roster.hoursCost')}</Box>
              </tr>
            </thead>
            <tbody>
              {employees.map((e) => (
                <tr key={e.employeeId}>
                  <Box component="td" style={tdStyle('start')}>
                    <Text fw={500} c="var(--wheels-ink-strong)">{empName(e)}</Text>
                    <Text size="xs" c="var(--wheels-ink-muted)">{e.positionAr}</Text>
                  </Box>
                  {days.map((d) => {
                    const iso = toIso(d);
                    const cells = grid.get(e.employeeId)?.get(iso) ?? [];
                    return (
                      <Box component="td" key={iso} style={tdStyle('center')}>
                        <Stack gap={4} align="stretch">
                          {cells.map((a) => (
                            <UnstyledButton key={a.shiftAssignmentId} onClick={() => openEdit(a)}
                              style={{
                                borderRadius: 6, padding: '4px 8px', textAlign: 'start',
                                background: (chipColor(a) ?? '#B5191D') + (a.status === 1 ? '' : '22'),
                                color: a.status === 1 ? '#fff' : 'var(--wheels-ink-strong)',
                                border: a.status === 1 ? 'none' : `1px dashed ${chipColor(a) ?? '#B5191D'}`,
                              }}>
                              <Text size="xs" fw={600} className="font-numeric">{hhmm(a.startTime)}–{hhmm(a.endTime)}</Text>
                              {tmplName(a) && <Text size="xs" style={{ opacity: 0.85 }}>{tmplName(a)}</Text>}
                            </UnstyledButton>
                          ))}
                          <Tooltip label={t('hr.scheduling.roster.addShift')} withinPortal>
                            <ActionIcon variant="subtle" color="gray" size="sm" style={{ alignSelf: 'center' }}
                              onClick={() => openCreate(e.employeeId, iso)} aria-label={t('hr.scheduling.roster.addShift')}>
                              <IconPlus size={14} stroke={1.8} />
                            </ActionIcon>
                          </Tooltip>
                        </Stack>
                      </Box>
                    );
                  })}
                  <Box component="td" style={tdStyle('end')}>
                    <Text size="sm" fw={600} className="font-numeric">{e.scheduledHours} {t('hr.scheduling.roster.hUnit')}</Text>
                    <Text size="xs" c="var(--wheels-ink-muted)" className="font-numeric">₪ {fmtMoney(e.estimatedCost)}</Text>
                  </Box>
                </tr>
              ))}
            </tbody>
          </Box>
        </Paper>
      )}

      <Drawer opened={drawer !== null} onClose={() => setDrawer(null)} position={ar ? 'left' : 'right'}
        size="md" padding="lg" title={drawer?.mode === 'edit' ? t('hr.scheduling.roster.editShift') : t('hr.scheduling.roster.addShift')}>
        <form onSubmit={submit}>
          <Stack gap="md">
            <Select label={t('hr.scheduling.roster.template')} data={templates.map((tpl: ShiftTemplateDto) => ({ value: tpl.shiftTemplateId, label: `${ar ? tpl.nameAr : tpl.nameEn ?? tpl.nameAr} (${hhmm(tpl.startTime)}–${hhmm(tpl.endTime)})` }))}
              value={form.values.shiftTemplateId} onChange={onTemplateChange} clearable searchable
              placeholder={t('hr.scheduling.roster.customShift')} />
            <Group grow>
              <TimeInput label={t('hr.scheduling.templates.startTime')} withAsterisk {...form.getInputProps('startTime')} />
              <TimeInput label={t('hr.scheduling.templates.endTime')} withAsterisk {...form.getInputProps('endTime')} />
            </Group>
            <Textarea label={t('hr.scheduling.roster.notes')} autosize minRows={2} {...form.getInputProps('notes')} />
            <Group justify="space-between" mt="sm">
              {drawer?.mode === 'edit' && drawer.assignment ? (
                <Button variant="subtle" color="red" leftSection={<IconTrash size={16} />}
                  onClick={async () => {
                    try { await del.mutateAsync(drawer.assignment!.shiftAssignmentId); setDrawer(null); }
                    catch { notifications.show({ color: 'red', title: t('common.toasts.errorTitle'), message: t('common.toasts.errorBody') }); }
                  }}>
                  {t('common.delete')}
                </Button>
              ) : <span />}
              <Group gap="sm">
                <Button variant="default" onClick={() => setDrawer(null)}>{t('common.cancel')}</Button>
                <Button type="submit" loading={create.isPending || update.isPending}>{t('common.save')}</Button>
              </Group>
            </Group>
          </Stack>
        </form>
      </Drawer>
    </Stack>
  );
}

function thStyle(align: 'start' | 'center' | 'end', width?: number): React.CSSProperties {
  return {
    textAlign: align, padding: '10px 12px', width, whiteSpace: 'nowrap',
    borderBottom: '1px solid var(--wheels-border)', background: 'var(--wheels-bg-subtle, transparent)',
    position: 'sticky', top: 0,
  };
}
function tdStyle(align: 'start' | 'center' | 'end'): React.CSSProperties {
  return {
    textAlign: align, padding: '8px 12px', verticalAlign: 'top',
    borderBottom: '1px solid var(--wheels-border)', minWidth: 90,
  };
}
